import { type FaqBlock } from './content.ts';

type FaqEntryProps = {
    question: string;
    answer: FaqBlock[];
    open: boolean;
    onToggle: () => void;
    /** Ties the question button to its answer panel for screen readers. */
    id: string;
};

const Chevron = ({ open }: { open: boolean }): JSX.Element => (
    <svg
        className={`w-5 h-5 flex-none transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M6 9l6 6 6-6"/>
    </svg>
);

const AnswerBlock = ({ block }: { block: FaqBlock }): JSX.Element => {
    switch (block.kind) {
        case 'orderedList':
            return (
                <ol className="list-decimal pl-6 flex flex-col gap-2">
                    {block.items.map((item) => <li key={item}>{item}</li>)}
                </ol>
            );
        case 'bulletList':
            return (
                <ul className="list-disc pl-6 flex flex-col gap-2">
                    {block.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
            );
        case 'paragraph':
        default:
            return <p>{block.text}</p>;
    }
};

const FaqEntry = ({ question, answer, open, onToggle, id }: FaqEntryProps): JSX.Element => (
    <div className="border-b border-white/10">
        <button
            type="button"
            id={`${id}-question`}
            aria-expanded={open}
            aria-controls={`${id}-answer`}
            onClick={onToggle}
            className="w-full flex items-center justify-between gap-5 py-6 text-left text-lg font-bold text-white hover:text-blue-600 transition"
        >
            <span>{question}</span>
            <Chevron open={open}/>
        </button>
        <div
            id={`${id}-answer`}
            role="region"
            aria-labelledby={`${id}-question`}
            className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}
        >
            <div className="overflow-hidden">
                <div className="flex flex-col gap-4 pb-7 pr-6 max-w-2xl opacity-70 leading-relaxed">
                    {answer.map((block, index) => (
                        <AnswerBlock key={`${id}-block-${index}`} block={block}/>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

export default FaqEntry;
